import * as React from 'react';

import {
  OrangeYellowDotGradient,
  RedSvgGradient,
  TurquoiseBlueGreenGradient,
} from '@/features/charts/components/chart-gradients';
import { useBudgetStatus } from '@/features/charts/hooks/use-budget-status';
import { cn } from '@/shared/utils/cn';

interface CurvedSliderProps {
  currValue: number;
  maxValue: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  children?: React.ReactNode;
  showPercentage?: boolean;
  filledColor?: string;
  emptyColor?: string;
  showThumb?: boolean;
  thumbColor?: string;
  thumbSize?: number;
}

export const CurvedSlider = ({
  currValue,
  maxValue,
  size = 60,
  strokeWidth = 4,
  className,
  showPercentage = true,
  filledColor = 'hsla(254, 32%, 60%, 1)',
  emptyColor = 'rgba(255, 255, 255, 0.15)',
  thumbSize = 18,
}: CurvedSliderProps) => {
  const { svgGradient, percentage } = useBudgetStatus(currValue, maxValue);

  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius;
  const strokeDasharray = circumference;
  const cappedValue = Math.min(percentage, 100);
  const strokeDashoffset = circumference - (cappedValue / 100) * circumference;

  const progress = cappedValue / 100;
  const angle = Math.PI - progress * Math.PI;
  const thumbX = size / 2 + radius * Math.cos(angle);
  const thumbY = size / 2 - radius * Math.sin(angle);

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center',
        className,
      )}
    >
      <svg
        width={size}
        height={size / 2 + strokeWidth / 2}
        viewBox={`0 0 ${size} ${size / 2 + strokeWidth / 2}`}
        className="overflow-visible"
      >
        <defs>
          <OrangeYellowDotGradient />
          <RedSvgGradient />
          <TurquoiseBlueGreenGradient />
        </defs>

        {/* Background arc (empty portion) */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke={emptyColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Progress arc (filled portion) */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke={filledColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-300 ease-in-out"
        />

        <circle
          cx={thumbX}
          cy={thumbY}
          r={thumbSize / 2}
          fill={svgGradient}
          stroke="white"
          strokeWidth="5"
          className="drop-shadow-sm transition-all duration-300 ease-in-out"
        />
      </svg>

      <div className="absolute inset-0 mb-3 flex items-end justify-center">
        {showPercentage && (
          <span className="text-2xl font-bold text-white">{percentage}%</span>
        )}
      </div>
    </div>
  );
};
